import { classCallCheck } from './class-call-check';
import { hasOwn } from './has-own';
import { inherit } from './inherit';

function mapDescriptors(props, fn) {
  return Object.keys(props).reduce((map, key) => {
    const desc = Object.getOwnPropertyDescriptor(props, key);
    map[key] = fn ? fn(desc) : desc;
    return map;
  }, {});
}

function addHiddenPropsToTarget(target, props) {
  Object.defineProperties(
    target,
    mapDescriptors(props, (descriptor) => {
      descriptor.enumerable = false;
      return descriptor;
    }),
  );
}

function assignProperties(target, props) {
  Object.defineProperties(target, mapDescriptors(props));
  return target;
}

/**
 * Used for subclassing. Invoke this method in the context of a superclass
 * to produce a subclass based on `props` and `classProps`.
 *
 * @this {Function} Constructor function.
 * @param {Object} props Instance properties for the subclass.
 * @param {Object} [props.constructor] Provide a custom constructor function
 * @param {Object} classProps Static properties for the subclass.
 * to use as the subclass.
 * @returns {constructor} A new subclass.
 */
function extend(props = {}, classProps = {}) {
  // eslint-disable-next-line @typescript-eslint/no-this-alias
  const SuperClass = this;
  const propsCopy = assignProperties({}, props);

  let SubClass;

  if (hasOwn(propsCopy, 'constructor')) {
    SubClass = propsCopy.constructor;
    delete propsCopy.constructor;
  } else {
    SubClass = function SubClassConstructor(...args) {
      classCallCheck(this, SubClass);
      SuperClass.apply(this, args);
    };
  }

  // inheritance of instance members
  inherit(SubClass, SuperClass);

  // inheritance of static members
  assignProperties(SubClass, SuperClass);

  // set instance properties
  addHiddenPropsToTarget(SubClass.prototype, propsCopy);

  // class properties
  assignProperties(SubClass, classProps);

  return SubClass;
}

export { extend };
